/**
 * Single source of truth for the community earnings model.
 *
 * These numbers are shown to prospective partners on the public landing page,
 * the revenue calculator and the signup flow. Nothing else in the codebase may
 * hard-code a tier, a share percentage or a fee — always import from here so the
 * marketing story and the product can never drift apart.
 *
 * Mirrors the "Communities x ShoUp" deck and the revenue calculator handoff.
 * Any change here must be signed off with finance.
 */

/** Transaction economics the estimate is built on. */
export const EARNINGS_ASSUMPTIONS = {
  /** Platform fee retained per transaction, in rupees, already net of GST. */
  netFeePerTransaction: 60,
  /** Gross fee charged to each side of a transaction — quoted in the disclaimer only. */
  grossFeePerSide: 35,
  /** Transactions a month made by a single active member. */
  transactionsPerActiveMember: 1,
};

/**
 * Share of members who must book within a calendar month for the performance
 * bonus to unlock, as a percentage.
 */
export const BONUS_THRESHOLD_PCT = 35;

/**
 * Earning tiers, ordered smallest to largest.
 *
 * `share`     — the community's cut of the net platform fee its members generate.
 * `bonusMax`  — the ceiling on the monthly performance bonus, in rupees.
 * `benchmark` — member count at which the full bonus is reached; the bonus scales
 *               linearly up to it. `null` means the tier always pays `bonusMax`.
 */
export const EARNING_TIERS = [
  {
    id: "tier-3",
    name: "Tier 3",
    minMembers: 0,
    maxMembers: 800,
    share: 0.1,
    bonusMax: 5000,
    benchmark: 800,
  },
  {
    id: "tier-2",
    name: "Tier 2",
    minMembers: 801,
    maxMembers: 1500,
    share: 0.2,
    bonusMax: 15000,
    benchmark: 1500,
  },
  {
    id: "tier-1",
    name: "Tier 1",
    minMembers: 1501,
    maxMembers: 3000,
    share: 0.3,
    bonusMax: 30000,
    benchmark: 3000,
  },
  {
    id: "marquee",
    name: "Marquee",
    minMembers: 3001,
    maxMembers: null,
    share: 0.35,
    bonusMax: 40000,
    benchmark: null,
  },
];

/** Slider bounds and starting positions for the public calculator. */
export const CALCULATOR_RANGES = {
  members: { min: 50, max: 4000, step: 10, default: 800 },
  bookingRate: { min: 5, max: 100, step: 1, default: 40 },
};

/** Ceiling on separately negotiated fixed sponsorship support, in rupees. */
export const SPONSORSHIP_MAX = 300000;

/**
 * Live proof points. Deliberately a constant so these can be bumped in one place
 * as the platform grows — they will age.
 */
export const PLATFORM_PROOF = {
  communities: 8,
  members: 120000,
  membersLabel: "1,20,000",
};

/** Session storage namespace for a lead's calculator inputs. */
export const CALCULATOR_STORAGE_KEY = "revenue_calculator";
